import { z } from 'zod';
import { http } from '@/lib/ats-https';
import { AiScoringSchemas } from '../../api/aiScoring.schema';
import { AxiosRequestConfig } from 'axios';

/**  generate icp id  api with zod schema */
const _generateIcpIdPayloadSchema = z.object({
  ai_scoring: z.object({
    job_description: z.string().min(1),
  }),
  errors: z.array(z.any()),
});

const generateIcpStatusResponseSchema = z.object({
  data: z.object({
    status: z.enum(['pending', 'success', 'error']),
    result_key: z.string(),
  }),
  errors: z.array(z.any()),
});

const BASE = '/ats_jobs';

async function generateIcpId(
  atsJobId: string,
  payload: z.infer<typeof generateIcpIdPayloadSchema>
): Promise<{
  data?: z.infer<typeof generateIcpStatusResponseSchema>;
  error?: Error;
}> {
  try {
    const response = await http.post(`${BASE}/${atsJobId}/ai_scoring`, payload);
    try {
      const data = generateIcpStatusResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log('🔄 Zod validation error generateIcpId:', error);
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

/**  fetch icp status api with zod schema */

const fetchIcpStatusResponseSchema = z.object({
  data: z.object({
    status: z.enum(['pending', 'success', 'error']),
    categories: z.array(z.string()).optional(),
    error: z.string().optional(),
    result_key: z.string(),
    icp: z.string().optional(),
    attributes: z.array(AiScoringSchemas.AiScoringAttributeSchema).optional(),
  }),
  errors: z.array(z.any()),
});

async function fetchIcpStatus(
  atsJobId: string,
  icpId: string
): Promise<{
  data?: z.infer<typeof fetchIcpStatusResponseSchema>;
  error?: Error;
}> {
  try {
    const response = await http.get(
      `${BASE}/${atsJobId}/ai_scoring/${icpId}/status`
    );
    try {
      const data = fetchIcpStatusResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log('🔄 Zod validation error fetchIcpStatus:', error);
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

/*  generate attributes from icp api with zod schema */

const _generateAttributesPayloadSchema = z.object({
  ai_scoring: z.object({
    icp: z.string().min(1),
  }),
  errors: z.array(z.any()),
});

const generateAttributesResponseSchema = z.object({
  data: z.object({
    status: z.enum(['pending', 'success', 'error']),
    result_key: z.string(),
    error: z.string().optional(),
    attributes: z.array(AiScoringSchemas.AiScoringAttributeSchema).optional(),
  }),
  errors: z.array(z.any()),
});

// POST /api_ats/v1/ats_jobs/{atsJobId}/ai_scoring/attributes/generate_from_icp
async function generateAttributes(
  atsJobId: string,
  payload: z.infer<typeof generateAttributesPayloadSchema>
): Promise<{
  data?: z.infer<typeof generateAttributesResponseSchema>;
  error?: Error;
}> {
  try {
    const response = await http.post(
      `${BASE}/${atsJobId}/ai_scoring/attributes`,
      payload
    );
    try {
      const data = generateAttributesResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log('🔄 Zod validation error generateAttributes:', error);
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

/**  fetch generate attributes status api with zod schema */

const fetchGenerateAttributesStatusResponseSchema = z.object({
  data: z.object({
    status: z.enum(['pending', 'success', 'error']),
    result_key: z.string(),
    error: z.string().optional(),
    attributes: z.array(AiScoringSchemas.AiScoringAttributeSchema).optional(),
  }),
  errors: z.array(z.any()),
});

async function fetchGenerateAttributesStatus(
  atsJobId: string,
  resultKey: string
): Promise<{
  data?: z.infer<typeof fetchGenerateAttributesStatusResponseSchema>;
  error?: Error;
}> {
  try {
    const response = await http.get(
      `${BASE}/${atsJobId}/ai_scoring/attributes/${resultKey}/status`
    );
    try {
      const data = fetchGenerateAttributesStatusResponseSchema.parse(response);
      return { data };
    } catch (error) {
      console.log(
        '🔄 Zod validation error fetchGenerateAttributesStatus:',
        error
      );
      return { error: error as Error };
    }
  } catch (error) {
    return { error: error as Error };
  }
}

/**  save icp  with zod schema */

const _saveIcpPayloadSchema = z.object({
  ai_scoring: z.object({
    icp: z.string().min(1),
    categories: z.array(z.string()).optional(),
  }),
  errors: z.array(z.any()),
});

const _saveIcpResponseSchema = z.object({
  data: z.object({
    message: z.string(),
    error: z.string().optional(),
    result_key: z.string().optional(),
  }),
  errors: z.array(z.any()),
});

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  fullResponse?: boolean;
}

async function saveIcp(
  atsJobId: string,
  payload: z.infer<typeof saveIcpPayloadSchema>
): Promise<{
  data?: z.infer<typeof saveIcpResponseSchema>;
  error?: Error;
  status?: number;
  errors?: any[];
}> {
  try {
    const response = await http.put(
      `${BASE}/${atsJobId}/ai_scoring/update`,
      payload,
      { fullResponse: true } as CustomAxiosRequestConfig
    );

    // since there is no response from the server, skip zod parsing
    return {
      data: response.data,
      status: response.status,
      // @ts-expect-error legacy untyped AI-scoring payload
      errors: response?.errors || [],
    };
  } catch (error: any) {
    return {
      error: error as Error,
      status: error?.response?.status,
      errors: error?.response?.data?.errors || [],
    };
  }
}

const getIcpCategories = async (atsJobId: string, icp: string) => {
  const response = await http.get(
    `${BASE}/${atsJobId}/ai_scoring/matching_categories?icp=${encodeURIComponent(
      icp
    )}`
  );
  return response.data;
};

export const getAiScoringApi = {
  saveIcp,
  generateIcpId,
  getIcpCategories,
  fetchIcpStatus,
  generateAttributes,
  fetchGenerateAttributesStatus,
};
